import domtoimage from 'dom-to-image';
import { jsPDF } from 'jspdf';

/**
 * PDF 다운로드 유틸리티
 */
export class PDFDownloader {
  constructor() {
    // A4 사이즈 (mm)
    this.A4_WIDTH = 210;
    this.A4_HEIGHT = 297;
    
    // PDF 여백 (mm) - 상하좌우 여백을 줄임
    this.MARGIN = 10;
    
    // 실제 콘텐츠 영역
    this.CONTENT_WIDTH = this.A4_WIDTH - (this.MARGIN * 2);
    this.CONTENT_HEIGHT = this.A4_HEIGHT - (this.MARGIN * 2);
  }

  /**
   * DOM 요소에서 모든 a 태그의 위치와 URL 정보를 추출
   * @param {HTMLElement} element - 추출할 요소
   * @returns {Array} 링크 정보 배열
   */
  extractLinks(element) {
    const links = [];
    const aElements = element.querySelectorAll('a[href]');
    
    aElements.forEach(aEl => {
      const rect = aEl.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      
      // 상대 위치 계산
      const relativeX = rect.left - elementRect.left;
      const relativeY = rect.top - elementRect.top;
      
      links.push({
        url: aEl.href,
        x: relativeX,
        y: relativeY,
        width: rect.width,
        height: rect.height,
        element: aEl
      });
    });
    
    return links;
  }

  /**
   * PDF 좌표계로 링크 위치를 변환
   * @param {Array} links - 링크 정보 배열
   * @param {number} scale - 스케일 비율
   * @param {number} offsetX - X 오프셋
   * @param {number} offsetY - Y 오프셋
   * @param {number} originalWidth - 원본 너비
   * @param {number} originalHeight - 원본 높이
   * @returns {Array} PDF 좌표계로 변환된 링크 정보
   */
  convertLinksToPDFCoordinates(links, scale, offsetX, offsetY, originalWidth, originalHeight) {
    const pixelToMm = 25.4 / 72;
    
    return links.map(link => {
      // 픽셀을 mm로 변환하고 스케일 적용
      const x = (link.x * pixelToMm * scale) + offsetX;
      const y = (link.y * pixelToMm * scale) + offsetY;
      const width = link.width * pixelToMm * scale;
      const height = link.height * pixelToMm * scale;
      
      return {
        url: link.url,
        x: x,
        y: y,
        width: width,
        height: height
      };
    });
  }

  /**
   * ::after 가상 요소와 특정 클래스의 ::before 가상 요소를 실제 DOM으로 변환
   * @param {HTMLElement} element - 변환할 요소
   */
  convertPseudoElements(element) {
    const elementsWithPseudo = element.querySelectorAll('*');
    const convertedElements = [];

    elementsWithPseudo.forEach(el => {
      // 특정 클래스의 ::before 가상 요소만 처리
      if (el.classList.contains('average-info') || el.classList.contains('my-info')) {
        const beforeStyle = window.getComputedStyle(el, '::before');
        const beforeContent = beforeStyle.getPropertyValue('content');
        const hasBeforeBackgroundImage = beforeStyle.backgroundImage && beforeStyle.backgroundImage !== 'none';
        
        if ((beforeContent && beforeContent !== 'none' && beforeContent !== '""' && beforeContent !== "''") || hasBeforeBackgroundImage) {
          // 원래 position 값 저장
          const originalPosition = el.style.position || '';
          
          // 부모 요소에 상대 위치 설정 (필요한 경우만)
          const computedPosition = window.getComputedStyle(el).position;
          if (computedPosition === 'static') {
            el.style.position = 'relative';
          }
          
          const beforeElement = document.createElement('span');
          beforeElement.className = 'pdf-pseudo-before';
          
          // 스타일 복사
          const styles = [
            'position', 'display', 'width', 'height', 
            'background-image', 'background-repeat', 'background-position', 
            'background-size', 'background-color', 'border', 'border-radius',
            'color', 'font-size', 'font-weight', 'text-align', 'line-height',
            'top', 'left', 'right', 'bottom', 'transform', 'z-index',
            'margin', 'padding', 'opacity'
          ];
          
          let cssText = '';
          styles.forEach(prop => {
            const value = beforeStyle.getPropertyValue(prop);
            if (value && value !== 'auto' && value !== 'normal') {
              cssText += `${prop}: ${value}; `;
            }
          });
          
          // 기본값 설정
          if (!beforeStyle.position || beforeStyle.position === 'static') {
            cssText += 'position: absolute; ';
          }
          if (!beforeStyle.display) {
            cssText += 'display: inline-block; ';
          }
          
          beforeElement.style.cssText = cssText;
          
          // content 값에서 따옴표 제거 후 텍스트로 설정
          if (beforeContent.startsWith('"') && beforeContent.endsWith('"')) {
            beforeElement.textContent = beforeContent.slice(1, -1);
          } else if (beforeContent.startsWith("'") && beforeContent.endsWith("'")) {
            beforeElement.textContent = beforeContent.slice(1, -1);
          }
          
          el.insertBefore(beforeElement, el.firstChild);
          convertedElements.push({ element: el, pseudoElement: beforeElement, originalPosition });
        }
      }

      // ::after 가상 요소 처리 (기존 로직 유지)
      const afterStyle = window.getComputedStyle(el, '::after');
      const afterContent = afterStyle.getPropertyValue('content');
      const hasAfterBackgroundImage = afterStyle.backgroundImage && afterStyle.backgroundImage !== 'none';
      
      if ((afterContent && afterContent !== 'none' && afterContent !== '""' && afterContent !== "''") || hasAfterBackgroundImage) {
        // 원래 position 값 저장
        const originalPosition = el.style.position || '';
        
        // 부모 요소에 상대 위치 설정 (필요한 경우만)
        const computedPosition = window.getComputedStyle(el).position;
        if (computedPosition === 'static') {
          el.style.position = 'relative';
        }
        
        const afterElement = document.createElement('span');
        afterElement.className = 'pdf-pseudo-after';
        
        // 스타일 복사
        const styles = [
          'position', 'display', 'width', 'height', 
          'background-image', 'background-repeat', 'background-position', 
          'background-size', 'background-color', 'border', 'border-radius',
          'color', 'font-size', 'font-weight', 'text-align', 'line-height',
          'top', 'left', 'right', 'bottom', 'transform', 'z-index',
          'margin', 'padding', 'opacity'
        ];
        
        let cssText = '';
        styles.forEach(prop => {
          const value = afterStyle.getPropertyValue(prop);
          if (value && value !== 'auto' && value !== 'normal') {
            cssText += `${prop}: ${value}; `;
          }
        });
        
        // 기본값 설정
        if (!afterStyle.position || afterStyle.position === 'static') {
          cssText += 'position: absolute; ';
        }
        if (!afterStyle.display) {
          cssText += 'display: inline-block; ';
        }
        
        afterElement.style.cssText = cssText;
        
        // content 값에서 따옴표 제거 후 텍스트로 설정
        if (afterContent.startsWith('"') && afterContent.endsWith('"')) {
          afterElement.textContent = afterContent.slice(1, -1);
        } else if (afterContent.startsWith("'") && afterContent.endsWith("'")) {
          afterElement.textContent = afterContent.slice(1, -1);
        }
        
        el.appendChild(afterElement);
        convertedElements.push({ element: el, pseudoElement: afterElement, originalPosition });
      }
    });

    return convertedElements;
  }

  /**
   * 변환된 가상 요소들을 제거
   * @param {Array} convertedElements - 변환된 요소들의 배열
   */
  removePseudoElements(convertedElements) {
    convertedElements.forEach(({ element, pseudoElement, originalPosition }) => {
      if (pseudoElement && pseudoElement.parentNode) {
        pseudoElement.parentNode.removeChild(pseudoElement);
      }
      
      // 원래 position 값 복원
      if (originalPosition !== undefined && element) {
        element.style.position = originalPosition;
      }
    });
  }

  /**
   * 요소를 이미지로 변환
   * @param {HTMLElement} element - 캡처할 DOM 요소
   * @param {Object} options - 캡처 옵션
   * @returns {Promise<string>} Base64 이미지 데이터
   */
  async captureElement(element, options = {}) {
    const defaultOptions = {
      // JPEG 품질(0~1). PNG는 jsPDF가 픽셀 단위로 전부 풀어 압축 없이 넣어서 느리고 용량도 커,
      // JPEG로 캡처해 그대로 넣는다. 0.95면 글자 흐림이 거의 보이지 않는다.
      quality: 0.95,
      bgcolor: '#ffffff',
      cacheBust: true,
      // 이미지를 못 받아오면(404 등) dom-to-image가 빈 data URL을 넣고 그 img가 로드 오류를 내서
      // PDF 전체가 실패한다. 받아오지 못한 이미지는 1x1 흰색 이미지로 대신 채워 나머지는 정상 출력한다.
      imagePlaceholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==',
      style: {
        transform: 'scale(1)',
        transformOrigin: 'top left'
      },
      filter: (node) => {
        // 불필요한 요소 필터링
        if (node.classList && node.classList.contains('no-pdf')) {
          return false;
        }
        return true;
      }
    };

    const finalOptions = { ...defaultOptions, ...options };

    // ::after 요소를 실제 DOM으로 변환
    const convertedElements = this.convertPseudoElements(element);

    try {

      
      // 짧은 대기 시간을 주어 DOM 업데이트 완료 보장
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const dataUrl = await domtoimage.toJpeg(element, finalOptions);

      
      return dataUrl;
    } catch (error) {
      console.error('요소 캡처 실패:', error);
      throw error;
    } finally {
      // 변환된 가상 요소들 정리
      this.removePseudoElements(convertedElements);
    }
  }

  /**
   * 이미지의 실제 크기를 가져옴
   * @param {string} dataUrl - Base64 이미지 데이터
   * @returns {Promise<{width: number, height: number}>}
   */
  getImageDimensions(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * 이미지를 A4 사이즈에 맞춰 스케일링 계산
   * @param {number} imgWidth - 이미지 너비
   * @param {number} imgHeight - 이미지 높이
   * @returns {{width: number, height: number, scale: number}}
   */
  calculateScaling(imgWidth, imgHeight) {
    // 픽셀을 mm로 변환 (72 DPI 기준)
    const pixelToMm = 25.4 / 72;
    
    const imgWidthMm = imgWidth * pixelToMm;
    const imgHeightMm = imgHeight * pixelToMm;

    // 콘텐츠 영역에 맞춰 스케일 계산
    const scaleX = this.CONTENT_WIDTH / imgWidthMm;
    const scaleY = this.CONTENT_HEIGHT / imgHeightMm;
    
    // 비율을 유지하면서 A4에 맞춤
    const scale = Math.min(scaleX, scaleY, 1); // 최대 100%

    const finalWidth = imgWidthMm * scale;
    const finalHeight = imgHeightMm * scale;

    return {
      width: finalWidth,
      height: finalHeight,
      scale: scale
    };
  }

  /**
   * 여러 페이지를 PDF로 생성
   * @param {Array<HTMLElement>} pages - 페이지 요소들
   * @param {string} filename - 파일명
   * @param {Function} onProgress - 진행률 콜백
   */
  async generatePDF(pages, filename = 'result.pdf', onProgress = null) {
    // dom-to-image는 페이지를 캡처할 때마다 문서의 @font-face 폰트(약 3.5MB)를 전부 다시 받아
    // base64로 바꾼다. 결과는 페이지와 무관하게 항상 같으므로, 이 PDF를 만드는 동안에는
    // 첫 페이지에서 만든 결과를 재사용한다. (첫 요청은 기존과 동일하게 cacheBust로 받는다)
    const fontFaces = domtoimage.impl && domtoimage.impl.fontFaces;
    const originalResolveAll = fontFaces && fontFaces.resolveAll;
    if (originalResolveAll) {
      let cachedFonts = null;
      fontFaces.resolveAll = function () {
        if (!cachedFonts) {
          cachedFonts = originalResolveAll.apply(this, arguments).catch((error) => {
            cachedFonts = null; // 실패한 결과는 재사용하지 않는다
            throw error;
          });
        }
        return cachedFonts;
      };
    }

    // 이미지(<img>)·CSS 배경 이미지도 dom-to-image가 요소마다, 페이지마다 새로 받는다.
    // cacheBust로 주소가 매번 달라져 브라우저 캐시도 못 쓰므로, 이 PDF를 만드는 동안에는
    // 같은 주소는 처음 받은 결과를 재사용한다. (이미지·CSS·폰트 모두 util.getAndEncode 하나를 거친다)
    const domUtil = domtoimage.impl && domtoimage.impl.util;
    const originalGetAndEncode = domUtil && domUtil.getAndEncode;
    if (originalGetAndEncode) {
      const encodedByUrl = new Map();
      // <img>는 절대 주소(http://.../img/a.png), CSS 배경은 상대 주소(/img/a.png)로 들어와
      // 같은 파일이 두 번 받아지므로 절대 주소로 맞춰서 저장한다.
      const toKey = (url) => {
        try {
          return new URL(url, window.location.href).href;
        } catch (e) {
          return url;
        }
      };
      domUtil.getAndEncode = function (url) {
        const key = toKey(url);
        if (!encodedByUrl.has(key)) {
          const request = originalGetAndEncode.apply(this, arguments).then((data) => {
            if (!data) encodedByUrl.delete(key); // 받기 실패한 결과는 재사용하지 않는다
            return data;
          });
          encodedByUrl.set(key, request);
        }
        return encodedByUrl.get(key);
      };
    }

    try {


      // 진행률(0~100)과 남은 시간(초)을 계산해 onProgress 로 넘긴다.
      // 페이지 캡처를 전체의 95%, 파일 저장을 나머지 5%로 본다.
      // 첫 페이지는 폰트를 받느라 느리므로, 두 페이지 이상 끝나면 첫 페이지를 뺀 평균으로 남은 시간을 추정한다.
      const totalPages = pages.length;
      const startedAt = Date.now();
      let firstPageMs = null;
      const reportProgress = (completed, message, percentOverride) => {
        if (!onProgress) return;
        let remainingSeconds = null;
        if (completed > 0 && completed < totalPages && firstPageMs !== null) {
          const elapsed = Date.now() - startedAt;
          const avgMs = completed >= 2 ? (elapsed - firstPageMs) / (completed - 1) : firstPageMs;
          remainingSeconds = Math.ceil((avgMs * (totalPages - completed)) / 1000);
        }
        onProgress({
          current: Math.min(completed + 1, totalPages), // 기존과 같이 '지금 처리 중인 페이지 번호'
          completed,
          total: totalPages,
          percent: percentOverride ?? Math.round((completed / totalPages) * 95),
          remainingSeconds,
          updatedAt: Date.now(),
          message
        });
      };

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        reportProgress(i, `페이지 ${i + 1} 캡처 중...`);



        // 페이지에서 링크 정보 추출
        const links = this.extractLinks(page);


        // 페이지 캡처
        const imageData = await this.captureElement(page, {
          width: page.scrollWidth,
          height: page.scrollHeight
        });

        // 이미지 크기 가져오기
        const { width: imgWidth, height: imgHeight } = await this.getImageDimensions(imageData);

        // A4에 맞춰 스케일링
        const { width: scaledWidth, height: scaledHeight, scale } = this.calculateScaling(imgWidth, imgHeight);

        // PDF에 이미지 추가
        if (i > 0) {
          pdf.addPage();
        }

        // 상단 정렬을 위한 위치 계산 (가로는 중앙, 세로는 상단)
        const x = this.MARGIN + (this.CONTENT_WIDTH - scaledWidth) / 2;
        const y = this.MARGIN;

        pdf.addImage(imageData, 'JPEG', x, y, scaledWidth, scaledHeight);

        if (i === 0) {
          firstPageMs = Date.now() - startedAt;
        }

        // 링크 영역을 PDF에 추가
        if (links.length > 0) {
          const pdfLinks = this.convertLinksToPDFCoordinates(
            links, 
            scale, 
            x, 
            y, 
            imgWidth, 
            imgHeight
          );
          
          pdfLinks.forEach(link => {
            try {
              // URL이 유효한지 확인
              if (link.url && link.url !== '#') {
                pdf.link(link.x, link.y, link.width, link.height, { url: link.url });

              }
            } catch (error) {
              console.warn(`링크 추가 실패: ${link.url}`, error);
            }
          });
        }


      }

      reportProgress(totalPages, 'PDF 파일 저장 중...');

      // PDF 다운로드
      pdf.save(filename);

      reportProgress(totalPages, '완료', 100);
      

      
      return {
        success: true,
        filename: filename,
        pages: pages.length
      };

    } catch (error) {
      console.error('PDF 생성 실패:', error);
      throw error;
    } finally {
      // 다음 PDF 생성 때는 폰트를 새로 읽도록 원래 함수로 되돌린다
      if (originalResolveAll) {
        fontFaces.resolveAll = originalResolveAll;
      }
      if (originalGetAndEncode) {
        domUtil.getAndEncode = originalGetAndEncode;
      }
    }
  }

  /**
   * 결과 페이지들을 자동으로 찾아서 PDF 생성
   * @param {string} containerSelector - 컨테이너 선택자
   * @param {string} pageSelector - 페이지 선택자
   * @param {string} filename - 파일명
   * @param {Function} onProgress - 진행률 콜백
   */
  async generateResultPDF(
    containerSelector = '.result-container',
    pageSelector = '.page-wrapper',
    filename = null,
    onProgress = null
  ) {
    try {
      const container = document.querySelector(containerSelector);
      if (!container) {
        throw new Error(`컨테이너를 찾을 수 없습니다: ${containerSelector}`);
      }

      const pageElements = container.querySelectorAll(pageSelector);
      if (pageElements.length === 0) {
        throw new Error(`페이지 요소를 찾을 수 없습니다: ${pageSelector}`);
      }

      // 기본 파일명 생성
      if (!filename) {
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
        filename = `학생결과_${dateStr}_${timeStr}.pdf`;
      }



      return await this.generatePDF(Array.from(pageElements), filename, onProgress);

    } catch (error) {
      console.error('결과 PDF 생성 실패:', error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스 export
export const pdfDownloader = new PDFDownloader();

// 간편 사용을 위한 함수들
export async function downloadResultPDF(options = {}) {
  const {
    filename = null,
    onProgress = null,
    containerSelector = '.result-container',
    pageSelector = '.page-wrapper'
  } = options;

  return await pdfDownloader.generateResultPDF(
    containerSelector,
    pageSelector,
    filename,
    onProgress
  );
}

export default PDFDownloader;